// /src/data/books/Cryptonomics/cap6.js
const CRYPTONOMICS_CAP6 = {
  id: "cryptonomics-cap6",
  slug: "premisa-de-la-moneda-digital",
  title: "Capítulo 6 — La Premisa de la Moneda Digital",
  language: "es",
  authors: ["Autor no especificado"],
  description:
    "Desarrolla la premisa central: en la práctica, una moneda es digital cuando su transferencia y liquidación pueden ejecutarse de extremo a extremo por medios digitales. Examina el papel de la banca en la digitalización del dinero, la historia de los fraudes y ataques, el modelo de seguridad (identidad, criptografía, canales), y los límites técnicos frente a la computación clásica y cuántica.",
  tags: [
    "moneda digital",
    "banca electrónica",
    "criptografía",
    "identidad digital",
    "fraude",
    "ciberseguridad",
    "pagos electrónicos"
  ],
  sections: [
    {
      id: "planteamiento",
      heading: "Planteamiento: ¿Qué es realmente una “moneda digital”?",
      text: `La vida económica contemporánea incluye bienes y servicios intangibles —telefonía móvil, internet, suscripciones de streaming— y comercio electrónico transfronterizo con liquidaciones casi instantáneas. 
En este contexto, la expresión “moneda digital” no alude a un nuevo material, sino a una **capacidad funcional**: que el valor pueda **moverse, validarse y asentarse** íntegramente por medios informáticos, sin presencia física del portador ni del emisor.
De aquí la premisa central de este capítulo: **Transacción digital ≈ Moneda digital**. Si una transacción puede originarse, autenticarse, transmitirse, verificarse y liquidarse digitalmente, entonces el instrumento usado opera, de hecho, como moneda digital.`
    },
    {
      id: "banca-y-digitalizacion",
      heading: "Cómo la banca convirtió al dinero en flujo digital",
      text: `Desde los primeros teletipos y módems hasta la banca por internet y móvil, el sistema financiero impulsó la informatización de pagos para operar en múltiples sucursales, reducir fraude documental y optimizar costos operativos. 
El salto clave fue **delegar al cliente** la capacidad de iniciar pagos desde su propio dispositivo (PC, smartphone), llevando el “mostrador” a la palma de la mano. 
En términos operativos, el banco mantiene el **libro contable** y el **sistema de liquidación**, mientras el usuario aporta autenticación (credenciales) y consentimiento (órdenes de pago) por canales digitales. 
Este reparto inauguró la era en la que el dinero —aun siendo fiduciario— **circula como mensajes firmados** entre identidades digitales.`
    },
    {
      id: "historia-de-ataques",
      heading: "Breve historia de ataques y fraudes en la era digital",
      text: `La digitalización trasladó el riesgo desde el papel falsificado hacia la **usurpación de identidades** y la **manipulación de canales**:
- **Intercepción y falsificación de señal** en enlaces tempranos (módem/teletipo).
- **Ingeniería de tonos** en telefonía analógica (ej. “blue boxes”) para burlar centrales.
- **Keyloggers y malware** para capturar credenciales en los inicios de la web pública.
- **Ataques MITM** (intermediario) sobre canales inseguros o mal configurados.
- **SIM swap** y secuestro de canales de segundo factor.
La respuesta bancaria fue elevar el listón técnico: **canales cifrados**, **tokens/2FA**, **chips EMV**, **HSMs** en el back-end y **monitoreo antifraude** en tiempo real.`
    },
    {
      id: "modelo-de-seguridad",
      heading: "Modelo de seguridad de la moneda digital",
      text: `Para que *transacción digital ≈ moneda digital* sea cierto en la práctica, se requieren tres capas coordinadas:
1) **Identidad y Autenticación**  
   - Identidad del cliente (KYC/alta) y del dispositivo (huella de clave/TPM/SE).  
   - Autenticación reforzada (algo que sabes, tienes y eres): contraseñas, OTP, app-token, biometría.
2) **Integridad y Confidencialidad (Criptografía)**  
   - Cifrado de canal extremo a extremo (p.ej., TLS) para que el mensaje no sea leído/alterado.  
   - **Firma/autorización** de la orden (no repudio) para vincular al titular con la instrucción.
3) **Orquestación Operativa**  
   - Motor de pagos, límites, listas de riesgo, analítica de comportamiento y conciliaciones.  
   - Reglas de liquidación y reversión, auditoría y trazabilidad completa.`
    },
    {
      id: "vectores-de-riesgo",
      heading: "Vectores de riesgo: dos grandes familias",
      text: `Aun con infraestructura robusta, los puntos débiles prácticos se concentran en:
- **Robo de credenciales del cliente**: phishing, malware, keylogging, SIM swap, shoulder surfing, fuga de datos.  
  *Mitigación*: MFA real (no solo SMS), notificaciones de firma, listas blancas, límites dinámicos, dispositivos de confianza, revocación ágil.
- **Compromiso criptográfico/canal**: degradar el cifrado, inyectar certificados falsos, explotar configuraciones débiles o vulnerabilidades.  
  *Mitigación*: políticas de cifrado actualizadas, pinning, HSM, rotación/gestión de llaves y pruebas periódicas (red teaming/cryptotesting).`
    },
    {
      id: "fortaleza-criptografica",
      heading: "Fortaleza criptográfica y costo de rompimiento",
      text: `En la banca minorista es estándar emplear **cifrado moderno de 256 bits** en canales y almacenamiento de claves, con suites y curvas recomendadas por estándares vigentes. 
A nivel práctico:
- Un **espacio de clave de 256 bits** es astronómico para fuerza bruta clásica; con hardware convencional, es **computacionalmente inviable**.  
- Longitudes de clave mayores (p.ej., 2048+ bits en criptografía asimétrica) elevan el costo del ataque pero también el costo computacional de cada operación.  
- La **computación cuántica** introduce riesgos teóricos (p.ej., Shor/Grover), por lo que el sector explora **criptografía poscuántica** y planes de migración por etapas. 
Conclusión operativa: la seguridad práctica depende de **políticas de llave**, **implementación**, **rotación** y **higiene del endpoint del usuario** tanto como de la longitud de la clave.`
    },
    {
      id: "equivalencia-moneda-transaccion",
      heading: "Moneda = Transacción: condiciones para la equivalencia",
      text: `La equivalencia “moneda digital” ↔ “transacción digital” es válida si se verifican simultáneamente:
- **Origen digital**: el ordenante inicia la instrucción desde un medio digital autenticado.  
- **Transporte confiable**: el mensaje viaja cifrado, íntegro y trazable.  
- **Autorización válida**: la orden queda firmada/confirmada de modo que el titular no pueda repudiarla.  
- **Liquidación efectiva**: el sistema abona/carga saldos en el libro mayor y deja rastro auditable.  
Si cualquiera de estos eslabones falla, el sistema deja de comportarse como “moneda digital” robusta y vuelve a depender de mecanismos manuales o de confianza extrínseca.`
    },
    {
      id: "riesgo-sistemico",
      heading: "Riesgo sistémico: qué ocurriría si cae la confianza",
      text: `La seguridad no es un absoluto técnico: es una **construcción socio-técnica**.  
- Un **ataque masivo exitoso** contra la banca podría provocar corridas hacia efectivo (limitado) y activos alternativos, degradando la función del dinero digital.  
- Congelaciones de cuentas, “corralitos” o rescates selectivos erosionan la credibilidad del sistema.  
- La percepción pública de que el banco no puede proteger patrimonio a mediano/largo plazo acelera la búsqueda de **sustitutos de confianza** (divisas duras, metales, cripto, stablecoins).`
    },
    {
      id: "sintesis",
      heading: "Síntesis operativa",
      text: `En la práctica cotidiana, **una moneda es digital cuando la transacción lo es**: si el valor puede moverse y liquidarse íntegramente por medios electrónicos con garantías de identidad, confidencialidad, integridad, autorización y auditabilidad. 
La cuestión económica relevante no es el “material” del dinero, sino la **capacidad confiable de ejecutar transacciones**. 
En esa capacidad se decide hoy la naturaleza del dinero, su alcance global y su rol en la vida diaria.`
    }
  ]
};

export default CRYPTONOMICS_CAP6;
